// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : upload components
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Progress} from 'semantic-ui-react'

// ---Framework imports
import {Sel, Actions} from 'uix/registries'
import log from 'uix/logging'
import {size, callIfFunc} from 'ldash'
import {Dropper, XMenu, VIcon} from 'uix/components'
import {useState, useSelector, useActions, usePartContext, useCallback, useEffect} from 'uix/hooks'

// ---Local imports
import {useUpload} from './hooks'
import {DirectoryTree} from './dirtree'

// ---Globals
export const MultiFileUploader = ({
  onFilesChange,
  renderFileName, // render func with default (fn, file) => fn
  menuItems,
  mimetypes,
}) => {
  let {uploadFiles, progress, uploaded} = useUpload()
  let [droppedFiles, setDroppedFiles] = useState([])

  let onFilesChanged = useCallback(droppedFiles => {
    setDroppedFiles(droppedFiles)
  })
  useEffect(() => {
    let files = droppedFiles.map(({path, state, size}) =>
      uploaded[path]
        ? {path, state: 'uploaded', size, uploadid: uploaded[path]}
        : {path, state, size}
    )
    callIfFunc(onFilesChange, files)
  }, [droppedFiles, uploaded])

  let dropperMenu = (
    <>
      <XMenu.Item
        icon={[
          'upload',
          ...(progress.running ? [{name: 'spinner', loading: true, color: 'green'}] : []),
        ]}
        content="Start upload"
        title="Upload files to server"
        disabled={droppedFiles.length == 0 || progress.running}
        onClick={() => uploadFiles(droppedFiles)}
      />
      {menuItems}
    </>
  )
  let renderFileStatus = useCallback(
    (fname, fdata) => {
      let icon = <VIcon name="minus" color="silver" />
      if (progress.activeUploads.some(u => u.fpath == fdata.path)) {
        icon = <VIcon name="spinner" color="green" loading />
      } else if (uploaded[fdata.path]) {
        icon = <VIcon name="check" />
      }
      return icon
    },
    [uploaded, progress.activeUploads]
  )

  return (
    <>
      <Dropper
        as="Menu"
        onFilesChanged={onFilesChanged}
        moreMenu={dropperMenu}
        mimetypes={mimetypes}
      />
      <DirectoryTree
        attached
        files={droppedFiles}
        collapseFiles={false}
        renderFileName={renderFileName}
        renderFileStatus={renderFileStatus}
      />
    </>
  )
}

export const SingleFileUploader = ({children, onUploadDone, onUploadProgress, ...props}) => {
  let {name: partName} = usePartContext()
  let [fileid, setFileid] = useState(null)
  let uploadInfo = useSelector(Sel.uploads)
  let uploadFile = useActions(Actions.uploadFile)

  let uploadedFile = uploadInfo[fileid] ?? {}

  let takeFile = ({path, bytes, size}) => {
    // path starts already with an / but a duplication is probably ok (for split below)
    let new_fileid = `${partName}/${path}`
    if (new_fileid != fileid) {
      let chunksize = Math.max(256 * 1024, parseInt(size / 10))
      uploadFile(new_fileid, bytes, chunksize)
      setFileid(new_fileid)
    }
  }

  useEffect(() => {
    if (uploadedFile.state == 'upload done' && onUploadDone) {
      onUploadDone({...uploadedFile, filename: fileid.split('/').at(-1)})
    }
  }, [uploadedFile.state])

  useEffect(() => {
    if (onUploadProgress) {
      let percentage = (uploadedFile.chunk / uploadedFile.chunks) * 100
      onUploadProgress({
        percentage,
        total: uploadedFile.size,
        active: uploadedFile.chunks !== uploadedFile.chunk,
      })
    }
  }, [uploadedFile.chunk])

  let onFilesChanged = useCallback(files => size(files) > 0 && takeFile(files[0]))
  return (
    <Dropper singleFile onFilesChanged={onFilesChanged} {...props}>
      {children}
    </Dropper>
  )
}
